import type { Motorcycle } from "../providers/vehiclesProvider";
import { Create, EditBase, Resource, Tab, useRedirect } from "react-admin";
import { MotorcycleForm } from "../details/vehicles/MotorcycleForm";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { useEffect } from "react";
import { Box } from "@mui/material";
import { Column } from "../misc/Column";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Tabs } from "../misc/Tabs";
import { VehicleCalendar } from "../details/vehicles/VehicleCalendar";
import { grants } from "../backoffice.access_control";
import { VehicleDetailsHeader } from "../details/vehicles/VehicleDetailsHeader";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { AccessDenied } from "../misc/AccessDenied";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { DefaultList } from "../misc/DefaultList";
import { VehicleDataCard } from "../details/vehicles/VehicleDataCard";

function CreateMotorcycleForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  useEffect(() => {
    if (!grants.has("Vehicle:create") && drivingSchoolId) {
      redirect(`/drivingSchools/${drivingSchoolId}/fleet#motorcycles`);
    }
  }, []);

  if (!grants.has("Vehicle:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create<Motorcycle>
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: ({ name }) => {
          // eslint-disable-next-line react-compiler/react-compiler
          window.onNavigation = () => {
            delete window.onNavigation;
            showSuccessToast(`Motorrad ${name} erfolgreich gespeichert`);
          };
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#motorcycles`);
        },
      }}
    >
      <MotorcycleForm mode="create" sx={{ padding: overviewCardStyle.padding }} />
    </Create>
  );
}

function MotorcycleDetails() {
  const redirect = useRedirect();
  const [{ drivingSchoolId }] = useAutovioContext();
  const motorcycle = useRecordAndSwitchDrivingSchoolIfNecessary<Motorcycle>((it) => it.drivingSchoolId);

  if (!motorcycle) {
    return <LoadingIndicator />;
  }

  return (
    <EditBase
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert");
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#motorcycles`);
        },
      }}
    >
      <Tabs
        sx={{ ...overviewCardStyle, padding: { xs: "5px 16px 16px", sm: "10px 30px" } }}
        header={<VehicleDetailsHeader />}
        mobileSelectAriaLabel="Fahrzeugbereich"
        syncWithLocation={false}
        syncWithHash
        separateCards
      >
        <Tab label="Kalender" value="calendar">
          <VehicleCalendar />
        </Tab>
        {grants.has("Vehicle:edit") && (
          <Tab label="Fahrzeug-Daten" value="data">
            <VehicleDataCard>
              <MotorcycleForm mode="edit" />
            </VehicleDataCard>
          </Tab>
        )}
      </Tabs>
    </EditBase>
  );
}

export const motorcyclesResource = (
  <Resource
    key="motorcycles"
    name="motorcycles"
    list={DefaultList}
    create={CreateMotorcycleForm}
    edit={MotorcycleDetails}
    options={{
      label: "Motorräder",
      createTitle: "Neues Motorrad",
      noEntriesText: "Noch kein Motorrad hinterlegt.",
      noMatchingEntriesText: "Keine passenden Motorräder gefunden.",
    }}
    recordRepresentation={(record: Motorcycle) => `${record.name} (${record.plateNumber})`}
  />
);
